
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import BookingLayout from "@/components/booking/BookingLayout";
import BookingServiceDetails from "@/components/booking/BookingServiceDetails";
import BookingDateSelection from "@/components/booking/BookingDateSelection";
import BookingTimeSelection from "@/components/booking/BookingTimeSelection";
import BookingSummary from "@/components/booking/BookingSummary";
import MembershipSelector from "@/components/booking/MembershipSelector";
import WaiverForm from "@/components/booking/WaiverForm";
import CouponSelector from "@/components/booking/CouponSelector";
import { useProviderById } from "@/hooks/useProviders";
import { useServices } from "@/hooks/useServices";
import { useAvailableTimeSlots } from "@/hooks/useAvailableTimeSlots";
import { useUserMemberships } from "@/hooks/useUserMemberships";
import { useProviderWaivers } from "@/hooks/useProviderWaivers";
import { useUserWaiverAgreements } from "@/hooks/useWaiverAgreements";
import { useAuth } from "@/hooks/useAuth";
import { useProfileCompletion } from "@/hooks/useProfileCompletion";
import { useTimeSlotRealtime } from "@/hooks/useTimeSlotRealtime";
import { useProviderAccess } from "@/hooks/useProviderAccess";
import { useBookingState } from "@/hooks/useBookingState";
import { useBookingHandlers } from "@/hooks/useBookingHandlers";
import { useActivePromotions } from "@/hooks/usePromotions";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const Booking = () => {
  const { providerId, serviceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  
  // Ref for scrolling to time selection on mobile
  const timeSelectionRef = useRef<HTMLDivElement>(null);

  // Custom hooks for state management
  const {
    selectedDate,
    setSelectedDate,
    selectedTimeSlot,
    setSelectedTimeSlot,
    selectedMembershipId,
    setSelectedMembershipId,
    selectedCoupon,
    setSelectedCoupon,
    searchModalOpen,
    setSearchModalOpen,
    processing,
    setProcessing,
    waiversCompleted,
    setWaiversCompleted,
  } = useBookingState();

  // Provider access check
  const { isProviderViewing } = useProviderAccess(providerId);

  // Data fetching hooks
  const { data: provider } = useProviderById(providerId || "");
  const { data: services } = useServices(providerId);
  const selectedService = services?.find(service => service.id === serviceId);
  
  // Profile completion check
  const { isProfileComplete, loading: profileLoading, refreshProfileStatus } = useProfileCompletion();
  
  // User memberships
  const { data: userMemberships, isLoading: membershipLoading } = useUserMemberships(providerId, serviceId);

  // Available time slots
  const { data: availableTimeSlots, isLoading: timeSlotsLoading } = useAvailableTimeSlots(
    serviceId, 
    selectedDate,
    isProviderViewing
  );

  // Real-time subscription for time slot updates
  useTimeSlotRealtime(serviceId, selectedDate);

  // Waivers
  const { data: waivers } = useProviderWaivers(providerId);
  const { data: existingAgreements } = useUserWaiverAgreements(providerId);

  // Waiver logic
  const hasActiveWaivers = waivers && waivers.length > 0;
  const allWaiversAgreed = hasActiveWaivers ? 
    waivers.every(waiver => existingAgreements?.some(agreement => agreement.waiver_id === waiver.id)) : 
    true;

  // Promotions
  const dateString = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined;
  const { data: promotions } = useActivePromotions(serviceId, dateString);
  const activePromotion = promotions && promotions.length > 0 ? promotions[0] : null;

  // Calculate promotion discount
  const calculatePromotionDiscount = (promotion: any, price: number) => {
    if (!promotion || !price) return 0;
    
    if (promotion.discount_type === 'percentage') {
      return (price * promotion.discount_percentage) / 100;
    } else {
      return Math.min(promotion.discount_amount, price);
    }
  };

  // Calculate discount and pricing
  const calculateCouponDiscount = (coupon: any) => {
    if (!coupon || !selectedService) return 0;
    
    const { discount_type, discount_value } = coupon.coupon;
    
    if (discount_type === 'weekly_credits' || discount_type === 'monthly_credits') {
      return selectedService.price || 0;
    }
    
    if (discount_type === 'fixed') {
      return Math.min(discount_value, selectedService.price || 0);
    } else {
      return Math.min(((selectedService.price || 0) * discount_value) / 100, selectedService.price || 0);
    }
  };

  const originalPrice = selectedService?.price || 0;
  const promotionDiscount = calculatePromotionDiscount(activePromotion, originalPrice);
  const couponDiscount = calculateCouponDiscount(selectedCoupon);
  
  // Apply promotion discount first, then coupon discount
  const priceAfterPromotion = originalPrice - promotionDiscount;
  const finalPrice = selectedMembershipId ? 0 : Math.max(0, priceAfterPromotion - couponDiscount);
  
  // Calculate total discount to pass to backend (promotion + coupon)
  const totalDiscount = promotionDiscount + couponDiscount;
  
  const isCreditBasedCoupon = selectedCoupon?.coupon.discount_type === 'weekly_credits' || selectedCoupon?.coupon.discount_type === 'monthly_credits';

  // Booking handlers
  const {
    handleBookingWithMembership,
    handleFreeBooking,
    handleRegularBooking,
  } = useBookingHandlers({
    providerId,
    serviceId,
    selectedDate,
    selectedTimeSlot,
    selectedMembershipId,
    selectedCoupon,
    availableTimeSlots,
    selectedService,
    userMemberships,
    hasActiveWaivers,
    waiversCompleted,
    couponDiscount: totalDiscount,
    isCreditBasedCoupon,
    setProcessing,
  });

  // Effects
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPayment(sessionId);
    }
  }, [searchParams]);

  useEffect(() => {
    setWaiversCompleted(allWaiversAgreed);
  }, [allWaiversAgreed]);

  const verifyPayment = async (sessionId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-payment', {
        body: { sessionId }
      });

      if (error) throw error;

      toast({
        title: "Booking Confirmed!",
        description: "Your booking has been confirmed and payment processed.",
      });
    } catch (error) {
      console.error('Payment verification error:', error);
      toast({
        title: "Error",
        description: "There was an issue verifying your payment. Please contact support.",
        variant: "destructive",
      });
    }
  };

  const handleProfileCompletion = async () => {
    await refreshProfileStatus();
  };

  // Function to scroll to time selection on mobile
  const handleScrollToTimeSelection = () => {
    if (timeSelectionRef.current) {
      timeSelectionRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  };

  // Form validation
  const isFormValid = selectedDate && selectedTimeSlot && user && waiversCompleted;
  const canBookWithMembership = selectedMembershipId && isFormValid;
  const canBookRegularly = !selectedMembershipId && isFormValid;
  const isFreeBooking = finalPrice === 0 && !selectedMembershipId;

  // Auth redirect - store provider ID in localStorage so it becomes favorite after signup
  if (!user) {
    if (providerId) {
      localStorage.setItem('pendingFavoriteProvider', providerId);
    }
    navigate(`/auth${providerId ? `?provider=${providerId}` : ''}`);
    return null;
  }

  // Loading state
  if (profileLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  const showProfilePrompt = isProfileComplete === false;

  // Brand styles from provider
  const brandStyles = provider ? {
    backgroundColor: provider.brand_background_color,
    font: provider.brand_font,
    headingColor: provider.brand_heading_color,
    bodyColor: provider.brand_body_color,
    primaryColor: provider.brand_primary_color,
    secondaryColor: provider.brand_secondary_color,
  } : undefined;

  // Create provider object for branded header
  const brandedProvider = provider ? {
    id: provider.id,
    name: provider.name,
    display_name: (provider as any).display_name,
    image_url: provider.image_url,
    slug: provider.slug,
    brand_primary_color: provider.brand_primary_color,
    brand_secondary_color: provider.brand_secondary_color,
    brand_font: provider.brand_font,
    brand_header_icon_text_color: (provider as any).brand_header_icon_text_color,
    brand_header_button_text_color: (provider as any).brand_header_button_text_color,
    brand_header_letter_spacing: (provider as any).brand_header_letter_spacing,
  } : undefined;

  return (
    <BookingLayout
      searchModalOpen={searchModalOpen}
      setSearchModalOpen={setSearchModalOpen}
      showProfilePrompt={showProfilePrompt}
      onProfileCompletion={handleProfileCompletion}
      onProfileCancel={() => navigate(-1)}
      isProviderViewing={isProviderViewing}
      brandStyles={brandStyles}
      provider={brandedProvider}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Booking Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Service Details */}
          <BookingServiceDetails 
            selectedService={selectedService}
            provider={provider}
            selectedDate={selectedDate}
          />

          {/* Waivers - Show first if user hasn't completed them */}
          {hasActiveWaivers && !waiversCompleted && (
            <WaiverForm
              providerId={providerId || ""}
              onAllWaiversAgreed={() => setWaiversCompleted(true)}
            />
          )}

          {/* Date Selection */}
          <BookingDateSelection
            selectedDate={selectedDate}
            onSelectDate={(date) => {
              setSelectedDate(date);
              setSelectedTimeSlot(""); // Reset selected time when date changes
            }}
            onDateSelected={handleScrollToTimeSelection}
          />

          {/* Time Selection */}
          {selectedDate && (
            <BookingTimeSelection
              ref={timeSelectionRef}
              selectedDate={selectedDate}
              selectedTimeSlot={selectedTimeSlot}
              onSelectTimeSlot={setSelectedTimeSlot}
              availableTimeSlots={availableTimeSlots}
              timeSlotsLoading={timeSlotsLoading}
            />
          )}

          {/* Coupon Selection - Show only if not using membership */}
          {!selectedMembershipId && selectedService && (
            <CouponSelector
              serviceId={serviceId}
              providerId={providerId}
              servicePrice={selectedService.price || 0}
              selectedCoupon={selectedCoupon}
              onCouponSelect={setSelectedCoupon}
              selectedDate={selectedDate}
            />
          )}

          {/* Membership Options */}
          {!membershipLoading && userMemberships && userMemberships.length > 0 && (
            <MembershipSelector
              memberships={userMemberships}
              selectedMembershipId={selectedMembershipId}
              onSelectMembership={(membershipId) => {
                setSelectedMembershipId(membershipId);
                if (membershipId) {
                  setSelectedCoupon(null); // Clear coupon when membership is selected
                }
              }}
              onPayWithMembership={handleBookingWithMembership}
            />
          )}
        </div>

        {/* Booking Summary */}
        <div className="lg:col-span-1">
          <BookingSummary
            selectedService={selectedService}
            provider={provider}
            selectedDate={selectedDate}
            selectedTimeSlot={selectedTimeSlot}
            availableTimeSlots={availableTimeSlots}
            selectedMembershipId={selectedMembershipId}
            selectedCoupon={selectedCoupon}
            couponDiscount={couponDiscount}
            promotionDiscount={promotionDiscount}
            activePromotion={activePromotion}
            finalPrice={finalPrice}
            hasActiveWaivers={hasActiveWaivers}
            waiversCompleted={waiversCompleted}
            canBookWithMembership={canBookWithMembership}
            canBookRegularly={canBookRegularly}
            isFreeBooking={isFreeBooking}
            processing={processing}
            onBookingWithMembership={handleBookingWithMembership}
            onFreeBooking={handleFreeBooking}
            onRegularBooking={handleRegularBooking}
          />
        </div>
      </div>
    </BookingLayout>
  );
};

export default Booking;
